import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    InputAdornment,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import moment, { Moment } from "moment";
import { useEffect, useRef, useState } from "react";
import { ApiFactory } from "../../../core/config/ApiFactory";
import { RealInput } from "../../../core/components/RealInput";
import { MetodoPagamento } from "../../../core/enum/MetodoPagamento";
import { IConta } from "../../interfaces/IConta";
import { IIntervaloCaixa } from "../../interfaces/IIntervaloCaixa";

interface Parcela {
    valor: string;
    dataVencimento: Moment | null;
}

interface ContaManualFormModalProps {
    open: boolean;
    onFechar: () => void;
    onSalvar: (dados: any | any[]) => void;
    conta?: IConta;
    loading?: boolean;
}

export const ContaManualFormModal = ({ open, onFechar, onSalvar, conta, loading }: ContaManualFormModalProps) => {
    const [descricao, setDescricao] = useState('');
    const [valor, setValor] = useState<any>(0);
    const [tipo, setTipo] = useState<'pagar' | 'receber'>('pagar');
    const [dataVencimento, setDataVencimento] = useState<Moment | null>(null);
    const [pago, setPago] = useState(false);
    const [metodoPagamento, setMetodoPagamento] = useState<string>('');
    const [intervaloCaixa, setIntervaloCaixa] = useState<IIntervaloCaixa | null>(null);
    const [inputIntervalo, setInputIntervalo] = useState('');
    const [intervalosOpcoes, setIntervalosOpcoes] = useState<IIntervaloCaixa[]>([]);
    const [buscandoIntervalos, setBuscandoIntervalos] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout>>();

    const [parcelamento, setParcelamento] = useState(false);
    const [numeroParcelas, setNumeroParcelas] = useState(2);
    const [parcelas, setParcelas] = useState<Parcela[]>([]);

    const buscarIntervalos = async (descricaoBusca: string) => {
        setBuscandoIntervalos(true);
        try {
            const params = new URLSearchParams({ paginaAtual: '1' });
            if (descricaoBusca) {
                params.set('descricao', descricaoBusca);
            } else {
                params.set('itensPorPagina', '5');
            }
            const { data } = await ApiFactory.getApi("MS_FINANCEIRO").get(`/caixa/intervalo?${params}`);
            setIntervalosOpcoes(data?.itens ?? []);
        } catch {
            setIntervalosOpcoes([]);
        } finally {
            setBuscandoIntervalos(false);
        }
    };

    const gerarParcelas = (qtd: number, valorTotal: number, dataBase: Moment | null): Parcela[] => {
        const valorParcela = Math.floor((valorTotal / qtd) * 100) / 100;
        const resultado: Parcela[] = [];
        let soma = 0;
        for (let i = 0; i < qtd; i++) {
            const isUltima = i === qtd - 1;
            const v = isUltima ? Math.round((valorTotal - soma) * 100) / 100 : valorParcela;
            soma += v;
            resultado.push({
                valor: String(v),
                dataVencimento: dataBase ? dataBase.clone().add(i, 'months') : null,
            });
        }
        return resultado;
    };

    useEffect(() => {
        if (conta) {
            setDescricao(conta.descricao ?? '');
            setValor(conta.valor ?? 0);
            setTipo(conta.tipo ?? 'pagar');
            setDataVencimento(conta.dataVencimento ? moment(conta.dataVencimento) : null);
            setPago(conta.pago ?? false);
            setMetodoPagamento(conta.metodoPagamento ?? '');
        } else {
            setDescricao('');
            setValor(0);
            setTipo('pagar');
            setDataVencimento(null);
            setPago(false);
            setMetodoPagamento('');
        }
        setParcelamento(false);
        setNumeroParcelas(2);
        setParcelas([]);
        setIntervaloCaixa(null);
        setInputIntervalo('');
        if (open) buscarIntervalos('');
    }, [conta, open]);

    const handleInputIntervaloChange = (_: any, valor: string, reason: string) => {
        setInputIntervalo(valor);
        if (reason !== 'input') return;
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            buscarIntervalos(valor);
        }, 500);
    };

    const getRotuloIntervalo = (intervalo: IIntervaloCaixa) => {
        if (intervalo.descricao) return intervalo.descricao;
        const abertura = moment(intervalo.abertoAs).format('DD/MM/YYYY HH:mm');
        const fechamento = intervalo.fechadoAs
            ? moment(intervalo.fechadoAs).format('DD/MM/YYYY HH:mm')
            : 'aberto';
        return `${abertura} - ${fechamento}`;
    };

    const handleToggleParcelamento = (ativo: boolean) => {
        setParcelamento(ativo);
        if (ativo) {
            setParcelas(gerarParcelas(numeroParcelas, parseFloat(valor) || 0, dataVencimento));
        } else {
            setParcelas([]);
        }
    };

    const handleNumeroParcelas = (qtd: number) => {
        setNumeroParcelas(qtd);
        if (parcelamento) {
            setParcelas(gerarParcelas(qtd, parseFloat(valor) || 0, dataVencimento));
        }
    };

    const handleValorChange = (novoValor: string) => {
        setValor(novoValor);
        if (parcelamento && parcelas.length > 0) {
            const total = parseFloat(novoValor) || 0;
            const valorParcela = Math.floor((total / numeroParcelas) * 100) / 100;
            let soma = 0;
            setParcelas(prev => prev.map((p, i) => {
                const isUltima = i === numeroParcelas - 1;
                const v = isUltima ? Math.round((total - soma) * 100) / 100 : valorParcela;
                soma += v;
                return { ...p, valor: String(v) };
            }));
        }
    };

    const handleDataVencimentoChange = (val: Moment | null) => {
        setDataVencimento(val);
        if (parcelamento && parcelas.length > 0) {
            setParcelas(prev => prev.map((p, i) => ({
                ...p,
                dataVencimento: val ? val.clone().add(i, 'months') : null,
            })));
        }
    };

    const handleParcelaValorChange = (index: number, novoValor: string) => {
        setParcelas(prev => prev.map((p, i) => i === index ? { ...p, valor: novoValor } : p));
    };

    const handleParcelaDataChange = (index: number, novaData: Moment | null) => {
        setParcelas(prev => prev.map((p, i) => i === index ? { ...p, dataVencimento: novaData } : p));
    };

    const parcelasValidas = parcelas.every(p => parseFloat(p.valor) > 0 && p.dataVencimento !== null);

    const handleSalvar = () => {
        if (!descricao) return;
        if (!valor || parseFloat(valor) <= 0) return;
        if (!tipo) return;

        const camposComuns = (d: any) => {
            if (pago && metodoPagamento) d.metodoPagamento = metodoPagamento;
            if (intervaloCaixa) d.intervaloCaixaUuid = intervaloCaixa.uuid;
            return d;
        };

        if (parcelamento) {
            if (!parcelasValidas) return;
            const lista = parcelas.map((p, i) => camposComuns({
                descricao: `${descricao} ${i + 1}/${numeroParcelas}`,
                valor: parseFloat(p.valor),
                tipo,
                dataVencimento: p.dataVencimento!.toISOString(),
                pago,
            }));
            onSalvar(lista);
            return;
        }

        if (!dataVencimento) return;

        onSalvar(camposComuns({
            descricao,
            valor: parseFloat(valor),
            tipo,
            dataVencimento: dataVencimento.toISOString(),
            pago,
        }));
    };

    const salvarDesabilitado = loading || !descricao || (parcelamento ? !parcelasValidas : !dataVencimento);

    return (
        <Dialog open={open} onClose={onFechar} maxWidth="sm" fullWidth>
            <DialogTitle>
                {conta ? 'Editar Conta' : 'Nova Conta'}
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '1rem', paddingTop: '1rem !important' }}>
                <TextField
                    label="Descrição"
                    value={descricao}
                    onChange={(e) => setDescricao(e.target.value)}
                    fullWidth
                    required
                    error={!descricao}
                    helperText={!descricao ? 'Descrição obrigatória' : ''}
                />
                <TextField
                    label="Valor total"
                    value={valor}
                    onChange={(e) => handleValorChange(e.target.value)}
                    fullWidth
                    required
                    InputProps={{
                        inputComponent: RealInput as any,
                    }}
                />
                <div>
                    <Typography variant="caption" color="text.secondary">Tipo *</Typography>
                    <Select
                        value={tipo}
                        onChange={(e) => setTipo(e.target.value as 'pagar' | 'receber')}
                        fullWidth
                        size="small"
                    >
                        <MenuItem value="pagar">A Pagar</MenuItem>
                        <MenuItem value="receber">A Receber</MenuItem>
                    </Select>
                </div>
                <DatePicker
                    label={parcelamento ? 'Vencimento da 1ª parcela' : 'Data de Vencimento'}
                    value={dataVencimento}
                    onChange={handleDataVencimentoChange}
                    format="DD/MM/YYYY"
                    slotProps={{ textField: { fullWidth: true, required: !parcelamento } }}
                />
                <Autocomplete
                    options={intervalosOpcoes}
                    value={intervaloCaixa}
                    inputValue={inputIntervalo}
                    loading={buscandoIntervalos}
                    filterOptions={(x) => x}
                    getOptionLabel={(op) => getRotuloIntervalo(op)}
                    isOptionEqualToValue={(op, val) => op.uuid === val.uuid}
                    onChange={(_, val) => setIntervaloCaixa(val)}
                    onInputChange={handleInputIntervaloChange}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Intervalo de Caixa (opcional)"
                            size="small"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {buscandoIntervalos ? <CircularProgress size={16} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    renderOption={(props, option) => (
                        <MenuItem {...props} key={option.uuid}>
                            {getRotuloIntervalo(option)}
                        </MenuItem>
                    )}
                    noOptionsText="Digite para buscar intervalos"
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={pago}
                            onChange={(e) => setPago(e.target.checked)}
                        />
                    }
                    label="Pago"
                />
                {pago && (
                    <div>
                        <Typography variant="caption" color="text.secondary">Método de Pagamento</Typography>
                        <Select
                            value={metodoPagamento}
                            onChange={(e) => setMetodoPagamento(e.target.value)}
                            fullWidth
                            size="small"
                            displayEmpty
                        >
                            <MenuItem value="">Selecione</MenuItem>
                            {Object.values(MetodoPagamento).map((m) => (
                                <MenuItem key={m} value={m}>{m}</MenuItem>
                            ))}
                        </Select>
                    </div>
                )}

                {!conta && (
                    <>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={parcelamento}
                                    onChange={(e) => handleToggleParcelamento(e.target.checked)}
                                />
                            }
                            label="Parcelado"
                        />

                        {parcelamento && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                        Número de parcelas:
                                    </Typography>
                                    <Select
                                        value={numeroParcelas}
                                        onChange={(e) => handleNumeroParcelas(Number(e.target.value))}
                                        size="small"
                                        sx={{ minWidth: 80 }}
                                    >
                                        {Array.from({ length: 35 }, (_, i) => i + 2).map(n => (
                                            <MenuItem key={n} value={n}>{n}x</MenuItem>
                                        ))}
                                    </Select>
                                </Box>

                                <Box sx={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem', pr: 0.5 }}>
                                    {parcelas.map((p, i) => (
                                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                                                Parcela {i + 1}/{numeroParcelas}
                                            </Typography>
                                            <TextField
                                                size="small"
                                                value={p.valor}
                                                onChange={(e) => handleParcelaValorChange(i, e.target.value)}
                                                InputProps={{
                                                    inputComponent: RealInput as any,
                                                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                                }}
                                                sx={{ width: 130 }}
                                            />
                                            <DatePicker
                                                value={p.dataVencimento}
                                                onChange={(val) => handleParcelaDataChange(i, val)}
                                                format="DD/MM/YYYY"
                                                slotProps={{ textField: { size: 'small', sx: { flex: 1 } } }}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onFechar} variant="outlined" disabled={loading}>
                    Cancelar
                </Button>
                <Button
                    onClick={handleSalvar}
                    variant="contained"
                    disabled={salvarDesabilitado}
                >
                    {loading ? <CircularProgress size={20} /> : (parcelamento ? `Salvar ${numeroParcelas} parcelas` : 'Salvar')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
